import React, { useRef, useState, useEffect } from 'react';
import './Cautions.css';
import DecryptedText from '../blocks/DecryptedText/DecryptedText';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import { Language, detectBrowserLanguage, getTranslations } from '../utils/localization';

const Cautions: React.FC = () => {
  // Language state
  const [language, setLanguage] = useState<Language>(detectBrowserLanguage());
  const translations = getTranslations(language);
  const cautionsTranslations = translations.cautions;
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  const [showBackToTop, setShowBackToTop] = useState(false);

  // Back to top visibility logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      <div className="project-page cautions-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div className="language-switcher">
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1>
              <DecryptedText 
                text={cautionsTranslations.title}
                animateOn="view"
                sequential={true}
                maxIterations={4}
                speed={60}
                className="decrypted-title"
                parentClassName="title-container"
                encryptedClassName="encrypted-title"
              />
            </h1>
            <hr />
            <h2>
              <DecryptedText 
                text={cautionsTranslations.subtitle}
                animateOn="view"
                sequential={true}
                maxIterations={1}
                speed={20}
                className="decrypted-description"
                parentClassName="description-container"
                encryptedClassName="encrypted-description"
              />
            </h2>
          </div>
        </header>

        <main className="project-content">
          <div className="container">
            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{cautionsTranslations.navItems[0]}</h3>
              <p>
                {cautionsTranslations.sections.overview}
              </p>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section iframe-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <div className="iframe-container">
                <iframe
                  src="https://isannabeth.com/portfolio/cautionsApp"
                  title="Cautions Application"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{cautionsTranslations.sections.features}</h3>
              <ul className="feature-list">
                <li>
                  <strong>{cautionsTranslations.features.cameraDetection.title}:</strong> 
                  {cautionsTranslations.features.cameraDetection.description}
                </li>
                <li>
                  <strong>{cautionsTranslations.features.mouseTracking.title}:</strong> 
                  {cautionsTranslations.features.mouseTracking.description}
                </li>
                <li>
                  <strong>{cautionsTranslations.features.dataVisualization.title}:</strong> 
                  {cautionsTranslations.features.dataVisualization.description}
                </li>
                <li>
                  <strong>{cautionsTranslations.features.containerized.title}:</strong> 
                  {cautionsTranslations.features.containerized.description}
                </li>
              </ul>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{cautionsTranslations.sections.architecture}</h3>
              <ul className="tech-list">
                <li>
                  <strong>{language === 'zh' ? "机器学习客户端" : "Machine Learning Client"}</strong> - {language === 'zh' ? "收集和分析摄像头和鼠标数据" : "Collects and analyzes camera and mouse data"}
                </li>
                <li>
                  <strong>{language === 'zh' ? "Web应用程序" : "Web Application"}</strong> - {language === 'zh' ? "使用Flask构建的用户界面" : "User interface built with Flask"}
                </li>
                <li>
                  <strong>{language === 'zh' ? "数据库" : "Database"}</strong> - {language === 'zh' ? "MongoDB存储传感器数据和分析结果" : "MongoDB for storing sensor data and analysis results"}
                </li>
                <li>
                  <strong>Docker</strong> - {language === 'zh' ? "容器化所有组件以便于部署" : "Containerizes all components for easy deployment"}
                </li>
                <li>
                  <strong>Python</strong> - {language === 'zh' ? "主要编程语言" : "Primary programming language"}
                </li>
              </ul>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{cautionsTranslations.navItems[3]}</h3>
              <p>
                {cautionsTranslations.sections.challenges}
              </p>
            </SpotlightCard>
          </div>
        </main>

        {/* Back to Top Button */}
        <div 
          className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </div>
      </div>
    </ClickSpark>
  );
};

export default Cautions; 